<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { getApiError } from '@/composables/getApiError'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { useUserStore } from '@/modules/user/stores/userStore'
import { integrationBaseUrl, webhooksApi } from '@/modules/settings/services/webhooksApi'
import WebhooksDeveloperGuide from '@/modules/settings/components/WebhooksDeveloperGuide.vue'
import type { PortalWebhook, WebhookForm, WebhookHistory } from '@/modules/settings/services/webhooksApi'

const props = defineProps<{ active: boolean }>()
const tab = ref('hooks')
const feedback = useFeedbackStore()
const user = useUserStore()
const hooks = ref<PortalWebhook[]>([])
const events = ref<Record<string, string>>({})
const configured = ref(false)
const loading = ref(false)
const busy = ref(false)
const error = ref('')
const next = ref<string | null>(null)
const editing = ref<PortalWebhook | null>(null)
const dialog = ref(false)
const ipText = ref('')
const secret = ref('')
const secretKey = ref('')
const secretDialog = ref(false)
const historyHook = ref<PortalWebhook | null>(null)
const history = ref<WebhookHistory | null>(null)
const historyError = ref('')
const confirmRotate = ref<PortalWebhook | null>(null)
const empty = (): WebhookForm => ({ name: '', direction: 'incoming', enabled: true, allowed_ips: [], target_url: '', events: [] })
const form = ref<WebhookForm>(empty())
const eventOptions = computed(() => Object.entries(events.value).map(([value, title]) => ({ value, title })))
const valid = computed(() => form.value.name.trim()
  && (form.value.direction === 'incoming' || (form.value.target_url.trim() && form.value.events.length)))
const stateNames = { pending: 'Ожидает', delivered: 'Доставлено', failed: 'Ошибка', cancelled: 'Отменено' }
const actionNames: Record<string, string> = {
  created: 'Создан', updated: 'Настройки изменены', revoked: 'Отключён', secret_rotated: 'Секрет заменён', delivery_retried: 'Назначена повторная доставка',
}
const isOwner = (hook: PortalWebhook) => hook.owner_id === user.user?.id
const date = (value: string) => new Date(value).toLocaleString('ru-RU')

async function load() {
  loading.value = true
  error.value = ''
  try {
    const [list, capabilities] = await Promise.all([webhooksApi.list(), webhooksApi.capabilities()])
    hooks.value = list.results
    next.value = list.next
    events.value = capabilities.events
    configured.value = capabilities.configured
  } catch (caught) {
    error.value = getApiError(caught, 'Не удалось загрузить интеграции')
  } finally {
    loading.value = false
  }
}
async function more() {
  if (!next.value || loading.value) return
  loading.value = true
  try {
    const result = await webhooksApi.next(next.value)
    hooks.value.push(...result.results)
    next.value = result.next
  } catch (caught) {
    error.value = getApiError(caught, 'Не удалось загрузить интеграции')
  } finally {
    loading.value = false
  }
}
function edit(hook: PortalWebhook | null) {
  editing.value = hook
  form.value = hook
    ? {
        name: hook.name,
        direction: hook.direction,
        enabled: hook.enabled,
        allowed_ips: [...hook.allowed_ips],
        target_url: hook.target_url,
        events: [...hook.events],
      }
    : empty()
  ipText.value = form.value.allowed_ips.join('\n')
  dialog.value = true
}
function showSecret(value: string, key: string) {
  secret.value = value
  secretKey.value = key
  secretDialog.value = true
}
async function save() {
  if (!valid.value || busy.value) return
  busy.value = true
  try {
    const payload: WebhookForm = { ...form.value, allowed_ips: ipText.value.split(/[\s,;]+/).filter(Boolean) }
    if (payload.direction === 'incoming') {
      payload.target_url = ''
      payload.events = []
    }
    if (editing.value) {
      await webhooksApi.update(editing.value.id, payload)
    } else {
      const result = await webhooksApi.create(payload)
      showSecret(result.secret, result.id)
    }
    dialog.value = false
    await load()
  } catch (caught) {
    feedback.showToast({ type: 'error', message: getApiError(caught, 'Не удалось сохранить хук') })
  } finally {
    busy.value = false
  }
}
async function toggle(hook: PortalWebhook) {
  if (busy.value) return
  busy.value = true
  try {
    await webhooksApi.update(hook.id, { enabled: !hook.enabled })
    await load()
  } catch (caught) {
    feedback.showToast({ type: 'error', message: getApiError(caught, 'Не удалось изменить состояние') })
  } finally {
    busy.value = false
  }
}
async function rotate() {
  const hook = confirmRotate.value
  if (!hook || busy.value) return
  busy.value = true
  try {
    const result = await webhooksApi.rotate(hook.id)
    confirmRotate.value = null
    showSecret(result.secret, hook.id)
  } catch (caught) {
    feedback.showToast({ type: 'error', message: getApiError(caught, 'Не удалось заменить секрет') })
  } finally {
    busy.value = false
  }
}
async function openHistory(hook: PortalWebhook) {
  historyHook.value = hook
  history.value = null
  historyError.value = ''
  try {
    const result = await webhooksApi.history(hook.id)
    if (historyHook.value?.id === hook.id) history.value = result
  } catch (caught) {
    if (historyHook.value?.id === hook.id) historyError.value = getApiError(caught, 'Не удалось загрузить журнал')
  }
}
async function retry(deliveryId: string) {
  if (!historyHook.value || busy.value) return
  busy.value = true
  try {
    await webhooksApi.retry(historyHook.value.id, deliveryId)
    await openHistory(historyHook.value)
  } catch (caught) {
    feedback.showToast({ type: 'error', message: getApiError(caught, 'Не удалось повторить доставку') })
  } finally {
    busy.value = false
  }
}
async function copy(value: string) {
  try {
    await navigator.clipboard.writeText(value)
    feedback.showToast({ type: 'success', message: 'Скопировано' })
  } catch {
    feedback.showToast({ type: 'error', message: 'Не удалось скопировать. Выделите и скопируйте текст вручную.' })
  }
}
watch(() => props.active, active => {
  if (active) load()
  else {
    secretDialog.value = false
    secret.value = ''
    dialog.value = false
    historyHook.value = null
  }
}, { immediate: true })
watch(secretDialog, visible => {
  if (!visible) {
    secret.value = ''
    secretKey.value = ''
  }
})
</script>

<template>
  <div class="pa-6">
    <div class="d-flex align-center flex-wrap ga-3 mb-4">
      <div class="flex-grow-1">
        <div class="text-h6">
          Интеграции и вебхуки
        </div>
      </div>
      <VBtn
        v-if="tab === 'hooks'"
        prepend-icon="ri-add-line"
        :disabled="!configured || loading"
        @click="edit(null)"
      >
        Добавить хук
      </VBtn>
    </div>
    <VTabs v-model="tab" class="mb-6" aria-label="Разделы интеграций">
      <VTab value="hooks">
        Хуки
      </VTab>
      <VTab value="developers">
        Разработчикам
      </VTab>
    </VTabs>
    <VWindow v-model="tab" :touch="false">
      <VWindowItem value="hooks">
        <VAlert
          v-if="error"
          type="error"
          variant="tonal"
          class="mb-4"
        >
          {{ error }}
        </VAlert>
        <VAlert
          v-if="!loading && !configured && !error"
          type="info"
          variant="tonal"
          class="mb-4"
        >
          Интеграции пока не активированы на сервере. Обратитесь в поддержку для настройки защищённого хранения секретов.
        </VAlert>
        <div v-if="!loading && !hooks.length" class="text-center pa-8 text-medium-emphasis">
          Хуки ещё не созданы
        </div>
        <VCard
          v-for="hook in hooks"
          :key="hook.id"
          variant="outlined"
          class="pa-4 mb-3"
        >
          <div class="d-flex align-center flex-wrap ga-3">
            <div class="flex-grow-1">
              <div class="text-subtitle-1">
                {{ hook.name }}
              </div>
              <div class="text-body-2">
                {{ hook.direction === 'incoming' ? 'Входящий API · сотрудники' : 'Исходящие события · заявки и НС' }}
              </div>
              <div class="text-caption text-medium-emphasis">
                Владелец: {{ hook.owner_name }}
              </div>
            </div>
            <VChip :color="hook.enabled && hook.owner_active ? 'success' : 'secondary'" size="small">
              {{ !hook.owner_active ? 'Нет прав у владельца' : hook.enabled ? 'Включён' : 'Отключён' }}
            </VChip>
            <VBtn size="small" variant="text" @click="openHistory(hook)">
              Журнал
            </VBtn>
            <VBtn
              v-if="isOwner(hook)"
              size="small"
              variant="text"
              :disabled="busy"
              @click="edit(hook)"
            >
              Настроить
            </VBtn>
            <VBtn
              v-if="isOwner(hook)"
              size="small"
              variant="text"
              :disabled="busy || !configured"
              @click="confirmRotate = hook"
            >
              Заменить секрет
            </VBtn>
            <VBtn
              v-if="hook.enabled || isOwner(hook)"
              size="small"
              variant="outlined"
              :disabled="busy"
              @click="toggle(hook)"
            >
              {{ hook.enabled ? 'Отключить' : 'Включить' }}
            </VBtn>
          </div>
          <div class="text-caption mt-3 text-break">
            ID: {{ hook.id }}
          </div>
          <div class="text-caption text-break">
            {{ hook.direction === 'incoming' ? 'Разрешённые IP отправителей' : 'Разрешённые IP получателя' }}: {{ hook.allowed_ips.length ? hook.allowed_ips.join(', ') : 'Без ограничения' }}
          </div>
          <div v-if="hook.direction === 'outgoing'" class="text-caption text-break">
            {{ hook.target_url }} · {{ hook.events.map(e => events[e] || e).join('; ') }}
          </div>
          <div v-else class="text-caption text-break">
            PATCH {{ integrationBaseUrl }}/v1/employees/{employee_id}/
          </div>
        </VCard>
        <VBtn
          v-if="next"
          variant="text"
          :loading="loading"
          @click="more"
        >
          Показать ещё
        </VBtn>
      </VWindowItem>
      <VWindowItem value="developers">
        <WebhooksDeveloperGuide />
      </VWindowItem>
    </VWindow>

    <VDialog v-model="dialog" max-width="680" :persistent="busy">
      <VCard :title="editing ? 'Настройка хука' : 'Новый хук'">
        <VCardText class="d-flex flex-column ga-4">
          <VTextField v-model="form.name" label="Название" maxlength="160" />
          <VSelect
            v-model="form.direction"
            label="Направление"
            :disabled="!!editing"
            :items="[{ title: 'Входящий API — сотрудники', value: 'incoming' }, { title: 'Исходящие события', value: 'outgoing' }]"
          />
          <template v-if="form.direction === 'outgoing'">
            <VTextField v-model="form.target_url" label="HTTPS URL получателя" placeholder="https://example.ru/webhook" />
            <VSelect
              v-model="form.events"
              label="События"
              :items="eventOptions"
              multiple
              chips
              closable-chips
            />
          </template>
          <VTextarea
            v-model="ipText"
            :label="form.direction === 'incoming' ? 'Разрешённые IP отправителей' : 'Разрешённые IP получателя'"
            rows="3"
            hint="Необязательно. Оставьте пустым для доступа с любого IP или укажите разрешённые IP/CIDR по одному на строку."
            persistent-hint
          />
          <VSwitch v-model="form.enabled" label="Включён" hide-details />
          <div v-if="editing" class="text-caption">
            Изменение IP, получателя, событий или состояния отменит ожидающие доставки.
          </div>
        </VCardText>
        <VCardActions>
          <VSpacer /><VBtn :disabled="busy" @click="dialog = false">
            Отмена
          </VBtn><VBtn :disabled="!valid" :loading="busy" @click="save">
            Сохранить
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog v-model="secretDialog" max-width="650" persistent>
      <VCard title="Сохраните секрет">
        <VCardText>
          <VAlert type="warning" variant="tonal" class="mb-4">
            Секрет показан один раз. После закрытия его можно только заменить. Передайте его в защищённое хранилище внешней системы.
          </VAlert>
          <div class="text-caption">
            ID хука — X-Daomed-Key
          </div><div class="text-break mb-3">
            {{ secretKey }}
          </div>
          <div class="text-caption">
            Секрет HMAC-SHA256
          </div><div class="text-break font-weight-medium mb-3">
            {{ secret }}
          </div>
          <VBtn variant="outlined" @click="copy(secret)">
            Скопировать секрет
          </VBtn>
        </VCardText>
        <VCardActions>
          <VSpacer /><VBtn @click="secretDialog = false">
            Я сохранил секрет
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog :model-value="!!confirmRotate" max-width="480" @update:model-value="value => { if (!value && !busy) confirmRotate = null }">
      <VCard title="Заменить секрет?">
        <VCardText>Прежний ключ сразу перестанет работать. Ожидающие доставки отменятся. Новый секрет нужно передать внешней системе.</VCardText>
        <VCardActions>
          <VSpacer /><VBtn :disabled="busy" @click="confirmRotate = null">
            Отмена
          </VBtn><VBtn :loading="busy" @click="rotate">
            Заменить
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog :model-value="!!historyHook" max-width="1000" @update:model-value="value => { if (!value) historyHook = null }">
      <VCard :title="`Журнал: ${historyHook?.name || ''}`">
        <VCardText>
          <VAlert v-if="historyError" type="error" variant="tonal">
            {{ historyError }}
          </VAlert>
          <VProgressLinear v-else-if="!history" indeterminate />
          <template v-else>
            <div class="text-caption mb-3">
              Последние 100 записей каждого типа. Тела запросов, ответов и секреты в журнале не показываются.
            </div>
            <VTable v-if="history.deliveries.length" density="compact">
              <thead><tr><th>Дата</th><th>Событие</th><th>Состояние</th><th>Попытки / HTTP</th><th /></tr></thead>
              <tbody>
                <tr v-for="item in history.deliveries" :key="item.id">
                  <td>{{ date(item.created_at) }}</td><td>{{ events[item.event] || item.event }}</td>
                  <td>
                    {{ stateNames[item.state] }}<div class="text-caption">
                      {{ item.error_code }}
                    </div>
                  </td>
                  <td>{{ item.attempts }} / {{ item.http_status || '—' }}</td>
                  <td>
                    <VBtn
                      v-if="item.state === 'failed' && historyHook && isOwner(historyHook) && historyHook.enabled"
                      size="small"
                      variant="text"
                      :disabled="busy"
                      @click="retry(item.id)"
                    >
                      Повторить
                    </VBtn>
                  </td>
                </tr>
              </tbody>
            </VTable>
            <div v-for="item in history.requests" :key="item.key" class="py-2 text-body-2">
              {{ date(item.created_at) }} · Сотрудник {{ item.employee_id }} · {{ item.changed_fields.join(', ') }}
            </div>
            <VDivider class="my-4" />
            <div class="text-subtitle-2 mb-2">
              Изменения настроек
            </div>
            <div v-for="(item, index) in history.management" :key="index" class="py-1 text-body-2">
              {{ date(item.created_at) }} · {{ actionNames[item.action] || item.action }} · {{ item.changed_fields.join(', ') }} · {{ item.actor_id }}
            </div>
          </template>
        </VCardText>
        <VCardActions>
          <VSpacer /><VBtn @click="historyHook = null">
            Закрыть
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
